import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  FileText, Download, Building2, Users, TrendingUp, Award, Calendar,
  BarChart3, RefreshCw, Eye, Printer, FileDown, PieChart
} from "lucide-react";

export default function PerformanceReports({ user }) {
  const [activeTab, setActiveTab] = useState("individual");
  const [loading, setLoading] = useState(false);
  
  // Period selection
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((currentDate.getMonth() + 1) / 3));
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [compareType, setCompareType] = useState("monthly");
  
  // Data
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [individualReport, setIndividualReport] = useState(null);
  const [departmentReport, setDepartmentReport] = useState(null);
  const [comparisonReport, setComparisonReport] = useState(null);
  const [awardsReport, setAwardsReport] = useState(null);
  const [scheduledReports, setScheduledReports] = useState([]);
  
  // Dialogs
  const [generateDialog, setGenerateDialog] = useState({ open: false, type: "" });
  const [generating, setGenerating] = useState(false);
  
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);
  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];
  const quarters = [
    { value: 1, label: "Q1 (Jan-Mar)" },
    { value: 2, label: "Q2 (Apr-Jun)" },
    { value: 3, label: "Q3 (Jul-Sep)" },
    { value: 4, label: "Q4 (Oct-Dec)" }
  ];

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchScheduledReports();
  }, []);

  useEffect(() => {
    if (activeTab === "individual" && selectedEmployee) {
      fetchIndividualReport();
    } else if (activeTab === "department") {
      fetchDepartmentReport();
    } else if (activeTab === "comparison") {
      fetchComparisonReport();
    } else if (activeTab === "awards") {
      fetchAwardsReport();
    }
  }, [activeTab, selectedYear, selectedMonth, selectedQuarter, selectedDepartment, selectedEmployee, compareType]);

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/employees-for-kra');
      setEmployees(res.data);
    } catch (error) {
      console.error("Failed to load employees");
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/employee-kra-assignments?include_history=true');
      const deptSet = new Set();
      res.data.forEach(a => {
        if (a.department_id) deptSet.add(a.department_id);
      });
      setDepartments(Array.from(deptSet));
    } catch (error) {
      console.error("Failed to load departments");
    }
  };

  const fetchIndividualReport = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const res = await api.get(`/performance/reports/individual/${selectedEmployee}?period_year=${selectedYear}&period_month=${selectedMonth}`);
      setIndividualReport(res.data);
    } catch (error) {
      toast.error("Failed to load individual report");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentReport = async () => {
    setLoading(true);
    try {
      let url = `/performance/reports/department?period_year=${selectedYear}`;
      if (selectedMonth) url += `&period_month=${selectedMonth}`;
      if (selectedDepartment !== "all") url += `&department=${selectedDepartment}`;
      const res = await api.get(url);
      setDepartmentReport(res.data);
    } catch (error) {
      toast.error("Failed to load department report");
    } finally {
      setLoading(false);
    }
  };

  const fetchComparisonReport = async () => {
    setLoading(true);
    try {
      let url = `/performance/reports/period-comparison?period_year=${selectedYear}&compare_type=${compareType}`;
      if (selectedDepartment !== "all") url += `&department=${selectedDepartment}`;
      const res = await api.get(url);
      setComparisonReport(res.data);
    } catch (error) {
      toast.error("Failed to load comparison report");
    } finally {
      setLoading(false);
    }
  };

  const fetchAwardsReport = async () => {
    setLoading(true);
    try {
      let url = `/performance/reports/awards-summary?period_year=${selectedYear}`;
      if (selectedDepartment !== "all") url += `&department=${selectedDepartment}`;
      const res = await api.get(url);
      setAwardsReport(res.data);
    } catch (error) {
      toast.error("Failed to load awards report");
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const res = await api.get(`/performance/reports/scheduled?period_year=${selectedYear}`);
      setScheduledReports(res.data);
    } catch (error) {
      console.error("Failed to load scheduled reports");
    }
  };

  const downloadIndividualPDF = async () => {
    if (!selectedEmployee) return;
    try {
      const response = await api.get(
        `/performance/reports/pdf/individual/${selectedEmployee}?period_year=${selectedYear}&period_month=${selectedMonth}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `performance_report_${selectedEmployee}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const downloadDepartmentPDF = async () => {
    try {
      let url = `/performance/reports/pdf/department?period_year=${selectedYear}`;
      if (selectedMonth) url += `&period_month=${selectedMonth}`;
      if (selectedDepartment !== "all") url += `&department=${selectedDepartment}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `department_report_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleGenerateReports = async (type) => {
    setGenerating(true);
    try {
      let endpoint = "";
      let body = {};
      
      if (type === "monthly") {
        endpoint = "/performance/reports/generate-monthly";
        body = { period_year: selectedYear, period_month: selectedMonth };
      } else if (type === "quarterly") {
        endpoint = "/performance/reports/generate-quarterly";
        body = { period_year: selectedYear, quarter: selectedQuarter };
      } else if (type === "annual") {
        endpoint = "/performance/reports/generate-annual";
        body = { period_year: selectedYear };
      }
      
      const res = await api.post(endpoint, body);
      toast.success(res.data.message);
      setGenerateDialog({ open: false, type: "" });
      fetchScheduledReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate reports");
    } finally {
      setGenerating(false);
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+': return 'bg-emerald-500 text-white';
      case 'A': return 'bg-green-500 text-white';
      case 'B+': return 'bg-lime-500 text-white';
      case 'B': return 'bg-yellow-500 text-white';
      case 'C': return 'bg-orange-500 text-white';
      case 'D': return 'bg-red-400 text-white';
      case 'F': return 'bg-red-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 100) return 'text-emerald-600';
    if (score >= 80) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  return (
    <Layout user={user} title="Performance Reports">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-headline-medium text-md3-on-surface">Performance Reports</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant">
              Generate and view performance reports, analytics, and trends
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setGenerateDialog({ open: true, type: "monthly" })}
              className="border-md3-outline rounded-md3-full"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Generate Monthly
            </Button>
            <Button
              variant="outline"
              onClick={() => setGenerateDialog({ open: true, type: "quarterly" })}
              className="border-md3-outline rounded-md3-full"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Generate Quarterly
            </Button>
            <Button
              onClick={() => setGenerateDialog({ open: true, type: "annual" })}
              className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Annual
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-md3-surface-container rounded-md3-full p-1">
            <TabsTrigger value="individual" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Users className="h-4 w-4 mr-2" />
              Individual
            </TabsTrigger>
            <TabsTrigger value="department" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Building2 className="h-4 w-4 mr-2" />
              Department
            </TabsTrigger>
            <TabsTrigger value="comparison" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <TrendingUp className="h-4 w-4 mr-2" />
              Period Comparison
            </TabsTrigger>
            <TabsTrigger value="awards" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Award className="h-4 w-4 mr-2" />
              Awards Summary
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <FileDown className="h-4 w-4 mr-2" />
              Generated Reports
            </TabsTrigger>
          </TabsList>

          {/* Individual Report Tab */}
          <TabsContent value="individual" className="space-y-4 mt-6">
            {/* Filters */}
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="p-4">
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label className="text-xs">Employee</Label>
                    <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>
                        {employees.map(e => (
                          <SelectItem key={e.id} value={e.employee_id || e.id}>
                            {e.first_name} {e.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Month</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {months.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchIndividualReport} disabled={!selectedEmployee || loading} className="bg-md3-primary text-md3-on-primary rounded-md3-full">
                    <Eye className="h-4 w-4 mr-2" />
                    View Report
                  </Button>
                  {individualReport && (
                    <Button onClick={downloadIndividualPDF} variant="outline" className="border-md3-primary text-md3-primary rounded-md3-full">
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Report Content */}
            {individualReport && (
              <div className="space-y-4">
                {/* Employee Info Card */}
                <Card className="bg-gradient-to-r from-md3-primary-container to-md3-secondary-container border-0 rounded-md3-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-md3-headline-small text-md3-on-primary-container">
                          {individualReport.employee?.first_name} {individualReport.employee?.last_name}
                        </h2>
                        <p className="text-md3-body-medium text-md3-on-primary-container/80">
                          {individualReport.employee?.department} | {individualReport.employee?.employee_id}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-md3-body-small text-md3-on-primary-container/70">Average Score</p>
                        <p className={`text-md3-display-small ${getScoreColor(individualReport.statistics?.average_score)}`}>
                          {individualReport.statistics?.average_score}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Total Reviews</p>
                      <p className="text-md3-headline-medium text-md3-on-surface">{individualReport.statistics?.total_summaries}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Total Earned</p>
                      <p className="text-md3-headline-medium text-md3-on-surface">{individualReport.statistics?.total_earned_weightage}%</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Awards</p>
                      <p className="text-md3-headline-medium text-amber-600">{individualReport.statistics?.total_awards}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Total Bonus</p>
                      <p className="text-md3-headline-medium text-emerald-600">{formatCurrency(individualReport.statistics?.total_bonus)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Performance Table */}
                {individualReport.summaries?.length > 0 && (
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large">Monthly Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead>Score</TableHead>
                            <TableHead>Grade</TableHead>
                            <TableHead>Earned</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {individualReport.summaries.map((s, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{months.find(m => m.value === s.period_month)?.label}</TableCell>
                              <TableCell className={getScoreColor(s.overall_score)}>{s.overall_score}%</TableCell>
                              <TableCell><Badge className={getGradeColor(s.performance_grade)}>{s.performance_grade}</Badge></TableCell>
                              <TableCell>{s.total_earned_weightage}%</TableCell>
                              <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Department Report Tab */}
          <TabsContent value="department" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="p-4">
                <div className="flex items-end gap-4">
                  <div className="w-48">
                    <Label className="text-xs">Department</Label>
                    <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="All Departments" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Month</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {months.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={downloadDepartmentPDF} variant="outline" className="border-md3-primary text-md3-primary rounded-md3-full">
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            {departmentReport && (
              <div className="space-y-4">
                <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-md3-title-large">Department Performance</CardTitle>
                      <div className="text-right">
                        <p className="text-md3-body-small text-md3-on-surface-variant">Overall Average</p>
                        <p className={`text-md3-headline-small ${getScoreColor(departmentReport.overall_average)}`}>
                          {departmentReport.overall_average}%
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Department</TableHead>
                          <TableHead>Employees</TableHead>
                          <TableHead>Avg Score</TableHead>
                          <TableHead>Reviews</TableHead>
                          <TableHead>A+/A</TableHead>
                          <TableHead>B+/B</TableHead>
                          <TableHead>C/D/F</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {departmentReport.departments?.map((d, idx) => {
                          const grades = d.grade_distribution || {};
                          return (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{d.department}</TableCell>
                              <TableCell>{d.employee_count}</TableCell>
                              <TableCell className={getScoreColor(d.average_score)}>{d.average_score}%</TableCell>
                              <TableCell>{d.summary_count}</TableCell>
                              <TableCell className="text-emerald-600">{(grades["A+"] || 0) + (grades["A"] || 0)}</TableCell>
                              <TableCell className="text-yellow-600">{(grades["B+"] || 0) + (grades["B"] || 0)}</TableCell>
                              <TableCell className="text-red-600">{(grades["C"] || 0) + (grades["D"] || 0) + (grades["F"] || 0)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Period Comparison Tab */}
          <TabsContent value="comparison" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="p-4">
                <div className="flex items-end gap-4">
                  <div className="w-36">
                    <Label className="text-xs">Compare By</Label>
                    <Select value={compareType} onValueChange={setCompareType}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label className="text-xs">Department</Label>
                    <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="All Departments" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {comparisonReport && (
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardHeader>
                  <CardTitle className="text-md3-title-large">
                    {compareType === "monthly" ? "Monthly" : "Quarterly"} Trend - {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {comparisonReport.comparison?.map((c, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-md3-surface-container rounded-md3-md">
                        <div className="w-16 font-medium text-md3-on-surface">{c.period}</div>
                        <div className="flex-1">
                          <Progress value={Math.min(c.average_score, 100)} className="h-4" />
                        </div>
                        <div className={`w-16 text-right font-medium ${getScoreColor(c.average_score)}`}>
                          {c.average_score}%
                        </div>
                        <div className="w-24 text-right text-md3-body-small text-md3-on-surface-variant">
                          {c.employee_count} employees
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Awards Summary Tab */}
          <TabsContent value="awards" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="p-4">
                <div className="flex items-end gap-4">
                  <div className="w-48">
                    <Label className="text-xs">Department</Label>
                    <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="All Departments" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {awardsReport && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Total Awards</p>
                      <p className="text-md3-headline-medium text-amber-600">{awardsReport.total_awards}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Total Bonus</p>
                      <p className="text-md3-headline-medium text-emerald-600">{formatCurrency(awardsReport.total_bonus)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Paid</p>
                      <p className="text-md3-headline-medium text-blue-600">{formatCurrency(awardsReport.paid_bonus)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4 text-center">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Pending</p>
                      <p className="text-md3-headline-medium text-orange-600">{formatCurrency(awardsReport.pending_bonus)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* By Award Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large">By Award Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Count</TableHead>
                            <TableHead>Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {awardsReport.by_award_type?.map((t, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{t.type}</TableCell>
                              <TableCell>{t.count}</TableCell>
                              <TableCell className="text-emerald-600">{formatCurrency(t.total_bonus)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large">By Department</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Department</TableHead>
                            <TableHead>Count</TableHead>
                            <TableHead>Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {awardsReport.by_department?.map((d, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{d.department}</TableCell>
                              <TableCell>{d.count}</TableCell>
                              <TableCell className="text-emerald-600">{formatCurrency(d.total_bonus)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Generated Reports Tab */}
          <TabsContent value="scheduled" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-title-large">Generated Reports</CardTitle>
                <CardDescription>Auto-generated and on-demand performance reports</CardDescription>
              </CardHeader>
              <CardContent>
                {scheduledReports.length === 0 ? (
                  <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                    <p>No reports generated yet</p>
                    <p className="text-md3-body-small mt-1">Use the buttons above to generate monthly, quarterly, or annual reports</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Generated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduledReports.slice(0, 50).map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{r.employee_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{r.report_type}</Badge>
                          </TableCell>
                          <TableCell>
                            {r.report_type === "monthly" && `${months.find(m => m.value === r.period_month)?.label} ${r.period_year}`}
                            {r.report_type === "quarterly" && `Q${r.quarter} ${r.period_year}`}
                            {r.report_type === "annual" && r.period_year}
                          </TableCell>
                          <TableCell className={getScoreColor(r.average_score || r.overall_score)}>
                            {r.average_score || r.overall_score}%
                          </TableCell>
                          <TableCell>
                            <Badge className={getGradeColor(r.performance_grade)}>{r.performance_grade}</Badge>
                          </TableCell>
                          <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                            {new Date(r.generated_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Generate Reports Dialog */}
      <Dialog open={generateDialog.open} onOpenChange={(open) => setGenerateDialog({ ...generateDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-md">
          <DialogHeader>
            <DialogTitle>Generate {generateDialog.type?.charAt(0).toUpperCase() + generateDialog.type?.slice(1)} Reports</DialogTitle>
            <DialogDescription>
              Generate performance reports for all employees
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {generateDialog.type === "monthly" && (
              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {months.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {generateDialog.type === "quarterly" && (
              <div className="space-y-2">
                <Label>Quarter</Label>
                <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {quarters.map(q => <SelectItem key={q.value} value={q.value.toString()}>{q.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialog({ open: false, type: "" })}>Cancel</Button>
            <Button onClick={() => handleGenerateReports(generateDialog.type)} disabled={generating} className="bg-md3-primary text-md3-on-primary">
              {generating ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
